import { getToken } from "commonFunctions/getToken";
import React, { useEffect, useMemo, useState } from "react";
import client from "../../utils/API/interceptor";
import * as c from "../../utils/constants/constants";
import { DocumentDetailContext } from "./contexts/DocumentDetailContext";
import { ConvertLocalToUtc, FormatDate } from "commonFunctions/commonFunctions";
import { PATIENT_CREATE_ACTIONS } from "../../redux/patientCreate/actions/actionCreators";
import { connect } from "react-redux";
import api from "utils/API/api";
import { useNavigate } from "react-router-dom";
import {
  getDocumentDetailData,
  getDocumentPreviewUrl,
  getDocumentDetailDataAfterUpdate,
  getClassificationMappingData,
  getDocumentId,
  exitDocument,
  uploadToOnbase,
  saveData,
  moveToCompleteStatus,
  moveToMarkedAsComplete
} from "./Service/api";
import GetFieldChanges from "./Service/handleChanges";


const DocumentDetailProvider = ({
  children,
  patient_create_details,
  update_patient_create_info,
  maximise_patient_create,
  patient_create,
  update_doc_info
}) => {

  const navigate = useNavigate();
  const [previousData, setPreviousData] = useState(null);
  const [documentData, setDocumentData] = useState(null);
  const [isSave, setDataSave] = useState(false);
  const [classificationMappingData, setClassificationMappingData] = useState(null);
  const [duplicateDocumentView, setDuplicateDocumentView] = useState(false);
  const [loader, setLoader] = useState(true);
  const [miniLoader, setMiniLoader] = useState(false);
  const [documentId, setDocumentId] = useState("");
  const [documentPreviewUrl, setDocumentPreviewUrl] = useState("");
  const [docSplit, setDocSplitData] = useState({
    isDocSplitEnabled: false,
    docSplitData: [],
    splitProgressPercentage: 0
  });
  const [dialogs, setDialogs] = useState({
    noDoc: false,
    flagNotesNotFilledWarning: false,
    blockSave: false,
    dataSave: false,
    spamAlert: false,
    spamFaxNumberValidation: false,
    discardWarning: false,
    duplicateWarning: false,
    notAvailableWarning: false,
    partialDocumentSplitWarning: false,
    splitMessage: false,
    uploadValidation: false,
    uploadWarning: false
  });

  // state provider values
  const stateValues = useMemo(() => ({
    _baseClassName: "document-detail",
    duplicateDocumentView,
    data: documentData,
    classificationMappingData,
    loader,
    miniLoader,
    patient_create_details,
    documentId,
    documentPreviewUrl,
    docSplit,
    dialogs,
    isSave,
    previousData
  }), [duplicateDocumentView, documentData, loader, miniLoader, classificationMappingData, documentPreviewUrl, docSplit, dialogs]);



  useEffect(() => {
    const Params = new URLSearchParams(window.location.search);
    let duplicate = Params.get('duplicate');
    const docId = Params.get("doc_id");

    document.title = Boolean(duplicate) ? c.PRODUCT_NAME + " - Duplicate Doc Review" : c.PRODUCT_NAME + " - Doc Review";

    setDuplicateDocumentView(Boolean(duplicate));
    setLoader(true);
    setDocumentId(docId);

    (async () => {
      localStorage.removeItem(c.ENC_DATA);
      localStorage.removeItem(c.ORDER_DATA);
      localStorage.removeItem(c.APP_DATA);
      const data = await getDocumentDetailData(docId);
      const categoryMapData = await getClassificationMappingData();
      setClassificationMappingData(categoryMapData);
      updateDocumentData(data);
      setLoader(false);
      setMiniLoader(false);
    })();

    return async () => {
      localStorage.removeItem(c.PREV_ROUTE);
    }

  }, []);

  useEffect(() => {
    if (patient_create_details?.patient_create_modal?.open
      && patient_create_details?.patient_create_modal?.message == "success"
    ) {
      setLoader(true);
      getDocumentDetailData(documentId);
    }
  }, [patient_create_details]);

  useEffect(() => {
    if (!documentPreviewUrl && documentId) {
      (async () => {
        let data = await getDocumentPreviewUrl(documentId);
        setDocumentPreviewUrl(data.signed_url)
      })();
    }
  }, [documentId]);

  const updateDocumentData = (apiData) => {
    let data = { ...apiData }
    const Params = new URLSearchParams(window.location.search);
    let duplicate = Params.get('duplicate');
    if (Boolean(duplicate)) navigate(`/${c.PAGE_URLS[c.PAGE_DATA]}/${c.PAGE_URLS[c.PAGE_DOC_MGMT]}/${c.PAGE_URLS[c.PAGE_MANUAL_REVIEW]}?doc_id=${data.ID}&duplicate=true`, { replace: true });
    else navigate(`/${c.PAGE_URLS[c.PAGE_DATA]}/${c.PAGE_URLS[c.PAGE_DOC_MGMT]}/${c.PAGE_URLS[c.PAGE_MANUAL_REVIEW]}?doc_id=${data.ID}`, { replace: true });

    let isSpamEnabled = (data.Tags || []).findIndex(m => m.tag_display_name == "SPAM" && m.enabled);

    let isDocSplit = false;
    let disableAllFlags = data.Entity_Details.docSplitData?.length ? true : false;

    let isFlagSelected = (data.Tags || []).some(m => m.enabled);
    (data.Tags || []).forEach(m => {
      if (m.tag_display_name == "DOC SPLIT" && (data.Page_Count == 1 || data.Entity_Details?.parentDocId)) m.disabled = true;
      if (m.tag_display_name == "DOC SPLIT" && m.enabled && data.Page_Count > 1) isDocSplit = true;
      if (isSpamEnabled != -1 && m.tag_display_name != "SPAM") m.disabled = true;
      if (disableAllFlags) m.disabled = true;

      if (data.Status == c.status.failed && [5001, 5002, 5003, 5004, 5005, 5006, 5007, 5008].includes(data.Error_Code) && !isFlagSelected) {
        m.disabled = false;
      }
    });

    if (isDocSplit) {
      if (!data.Entity_Details.hasOwnProperty("enable_doc_split") && data.Entity_Details.docSplitData?.length) data.Entity_Details.enable_doc_split = true; // enable split button if one child available
      else if (data.Entity_Details.docSplitData?.length == 0) delete data.Entity_Details.enable_doc_split; // remove button incase no child records are added
    } else {
      if (data.Entity_Details.hasOwnProperty("enable_doc_split")) delete data.Entity_Details.enable_doc_split // remove button incase if doc split flag removed
    }
    setDocSplitData(prevState => ({
      ...prevState,
      docSplitData: data?.Entity_Details?.docSplitData || [],
      isDocSplitEnabled: isDocSplit,
      splitProgressPercentage: getSplitProgressPercentage(data?.Entity_Details?.docSplitData, data.Page_Count)
    }));
    setDocumentData(JSON.parse(JSON.stringify(data)));
    setPreviousData(JSON.parse(JSON.stringify(data)));
    setDocumentId(data.ID);
    setLoader(false);
    setMiniLoader(false);
  }

  const getSplitProgressPercentage = (data = [], pageCount) => {
    let pageRanges = [];
    data.forEach(m => {
      for (let i = m.start; i <= m.end; i++) {
        if (!pageRanges.includes(i)) pageRanges.push(i);
      }
    });
    return pageCount && Math.ceil((pageRanges.length / pageCount) * 100) || 0;
  }

  const gotToDocMgmt = async () => {
    var filter_data = JSON.parse(localStorage.getItem('is_filter'));
    filter_data?.isFilter ? localStorage.setItem('from_details', 'true') : localStorage.setItem('from_details', 'false');
    let temp = await exitDoc(documentId);
    navigate(`/${c.PAGE_URLS[c.PAGE_DATA]}/${c.PAGE_URLS[c.PAGE_DOC_MGMT]}`);
  }

  const getDocId = async (step) => {
    setLoader(true);
    const Params = new URLSearchParams(window.location.search);
    let doc_id = Params.get('doc_id');
    try {
      let data = await getDocumentId(step);
      if (data?.length) {
        setDialogs(prevState => ({
          ...prevState,
          not_available_warning: false
        }));
        let temp = await exitDoc(doc_id);
        let docData = await getDocumentDetailData(data[0].doc_id);
        updateDocumentData(docData);
        navigate(`/${c.PAGE_URLS[c.PAGE_DATA]}/${c.PAGE_URLS[c.PAGE_DOC_MGMT]}/${c.PAGE_URLS[c.PAGE_MANUAL_REVIEW]}?doc_id=${docData.ID}`, { replace: true });
      } else {
        setLoader(false)
        setDialogs(prevState => ({
          ...prevState,
          noDoc: true
        }));
        setTimeout(() => {
          setDialogs(prevState => ({
            ...prevState,
            noDoc: false
          }));
        }, 2000);
      }
    } catch (err) {
      setLoader(false);
    }
  };

  const exitDoc = async () => {
    if (!(localStorage.getItem(c.LOGOUT) === "inactive")) {
      const Params = new URLSearchParams(window.location.search);
      let doc_id = Params.get('doc_id');
      return await exitDocument(doc_id)
    }
  }

  const uploadDocumentToOnbase = async () => {
    var comp = c.UI_COMP.document.toString();
    var action = c.UI_ACTION.Document_uploaded_to_onbase;
    var query = {
      "doc_id": documentId,
      "revision_id": documentData.Revision_ID
    }
    setMiniLoader(true);
    try {
      await uploadToOnbase(query, comp, action);
      let docData = await getDocumentDetailDataAfterUpdate(documentId);
      updateDocumentData(JSON.parse(JSON.stringify(docData)));
      setDocumentData(JSON.parse(JSON.stringify(docData)));
    } catch (error) {
      if (error?.response?.status == 409) {
        setDialogs(prevState => ({
          ...prevState,
          blok_save: true
        }));
      }
      setMiniLoader(false);
    }
  }

  const completeDocument = async () => {
    var comp = c.UI_COMP.document.toString();
    var action = c.UI_ACTION.Document_completed;
    var query = {
      "doc_id": documentId,
      "revision_id": documentData.Revision_ID
    }
    setMiniLoader(true);
    try {
      await moveToCompleteStatus(query, comp, action);
      let docData = await getDocumentDetailDataAfterUpdate(documentId);
      updateDocumentData(JSON.parse(JSON.stringify(docData)));
      setDocumentData(JSON.parse(JSON.stringify(docData)));
    } catch (error) {
      if (error?.response?.status == 409) {
        setDialogs(prevState => ({
          ...prevState,
          blok_save: true
        }));
      }
      setMiniLoader(false);
    }
  }

  const skipUpload = async () => {
    var comp = c.UI_COMP.document.toString();
    var action = c.UI_ACTION.Document_marked_as_completed;
    var query = {
      "doc_id": documentId,
      "revision_id": documentData.Revision_ID
    }
    setMiniLoader(true);
    try {
      await moveToMarkedAsComplete(query, comp, action);
      let docData = await getDocumentDetailDataAfterUpdate(documentId);
      updateDocumentData(JSON.parse(JSON.stringify(docData)));
      setDocumentData(JSON.parse(JSON.stringify(docData)));
    } catch (error) {
      if (error?.response?.status == 409) {
        setDialogs(prevState => ({
          ...prevState,
          blok_save: true
        }));
      }
      setMiniLoader(false);
    }
  }

  const saveAllData = async (type, proceedWithoutFlagNotes) => {
    let res = JSON.parse(JSON.stringify(documentData));
    if (res.Entity_Details.ehrDetails) {
      if (res.Entity_Details.classify.documentLevel === "Encounter") {
        res.Entity_Details.ehrDetails["order"] = {};
      } else if (res.Entity_Details.classify.documentLevel === "Order") {
        res.Entity_Details.ehrDetails["encounter"] = {};
      } else if (res.Entity_Details.classify.documentLevel === "Patient") {
        res.Entity_Details.ehrDetails["order"] = {};
        res.Entity_Details.ehrDetails["encounter"] = {};
      }
    }

    if (type == "duplicate") res.Entity_Details.notifications = {};

    if (docSplit?.docSplitData?.length) res.Entity_Details.docSplitData = docSplit?.docSplitData;
    else if (res.Entity_Details.docSplitData?.length && !res.Entity_Details.hasOwnProperty("enable_doc_split")) res.Entity_Details.enable_doc_split = true;

    let { notes, selected_tags } = res.Entity_Details || {};
    let flagCheck = res.Tags && res.Tags.findIndex(m => m.enabled);
    document.activeElement?.blur();
    if (type == "save" && !proceedWithoutFlagNotes && (flagCheck > -1 && !notes)) {
      setDialogs(prevState => ({
        ...prevState,
        flagNotesNotFilledWarning: true
      }));
      return false
    }

    let fieldsUpdated = GetFieldChanges({ ...previousData }, res, type);
    res.Entity_Details.fields_updated = fieldsUpdated;
    try {
      let response = await saveData(res.Entity_Details, documentId, documentData.Revision_ID, type);
      if (response.status == 200) {
        setMiniLoader(false);
        setDataSave(false);

        if (type != 'pm' && type != "duplicate") {
          setDialogs(prevState => ({
            ...prevState,
            dataSave: true
          }));
          setTimeout(() => {
            setDialogs(prevState => ({
              ...prevState,
              dataSave: false
            }));
          }, 1000);
        }
        let data = await getDocumentDetailDataAfterUpdate(documentId);
        updateDocumentData({ ...data });
      }
    } catch (error) {
      if (error.response.status === 409) {
        setDialogs(prevState => ({
          ...prevState,
          blockSave: true
        }));
      }
      setMiniLoader(false);
      setDataSave(false);
    }
  }

  const splitDocument = () => {
    var res = JSON.parse(JSON.stringify(documentData));
    let data = JSON.parse(JSON.stringify(res.Entity_Details.docSplitData || []));
    data.forEach(m => {
      m.dob = FormatDate({ date: m.dob, format: 'YYYY-MM-DD' })
      m.start = Number(m.start);
      m.end = Number(m.end);
      m.document_status = "Pending"
      delete m.id;
    });
    setMiniLoader(false);
    api.splitDocument({
      doc_id: Number(documentId),
      doc_split_data: data
    })
      .then(async (res) => {
        if (res.status == 200) {
          let data = await getDocumentDetailDataAfterUpdate(documentId);
          updateDocumentData({ ...data });
          setDialogs(prevState => ({
            ...prevState,
            splitMessage: true
          }));
          setTimeout(() => {
            setDialogs(prevState => ({
              ...prevState,
              splitMessage: false
            }));
          }, 1000);
        }
      })
      .catch((error) => {
        if (error.response.status === 409) {
          setDialogs(prevState => ({
            ...prevState,
            blockSave: true
          }));
        }
        setMiniLoader(false);
      });
  }
  const stateMethods = {
    setLoader,
    setMiniLoader,
    setDocumentData,
    update_patient_create_info,
    maximise_patient_create,
    patient_create,
    update_doc_info,
    getDocId,
    gotToDocMgmt,
    getDocumentDetailData,
    getDocumentDetailDataAfterUpdate,
    updateDocumentData,
    setDialogs,
    saveAllData,
    setDataSave,
    setDocSplitData,
    getSplitProgressPercentage,
    splitDocument,
    uploadDocumentToOnbase,
    completeDocument,
    skipUpload
  }

  return (
    <DocumentDetailContext.Provider
      value={{
        ...stateValues,
        ...stateMethods
      }}
    >
      {children}
    </DocumentDetailContext.Provider>
  );
};


const mapStateToProps = (state) => {
  return {
    patient_create_details: state.patient_create || {}
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    patient_create: (action) => dispatch(PATIENT_CREATE_ACTIONS.TOGGLE_PATIENT_CREATE_POPUP(action)),
    maximise_patient_create: (action) => dispatch(PATIENT_CREATE_ACTIONS.MAXIMISE_PATIENT_CREATE_POPUP(action)),
    update_patient_create_info: (action) => dispatch(PATIENT_CREATE_ACTIONS.UPDATE_PATIENT_CREATE_DETAILS(action)),
    update_doc_info: (action) => dispatch(PATIENT_CREATE_ACTIONS.UPDATE_DOC_DETAILS(action)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(DocumentDetailProvider);
